/**
 * Pure derive: CoreHousehold → DerivedHouseholdView.
 * Never persist this output — recompute from core on hydrate / refresh.
 */

import type {
  CoreAccount,
  CoreHousehold,
  CoreTransaction,
  DeriveHouseholdOptions,
  DerivedHouseholdView,
  DollarAllocationBucket,
  HouseholdPeriodKind,
  NetWorthYearRow,
  SampleAsset,
  SampleLiability,
  SampleNetWorth,
  SampleProfile,
  SampleProjections,
  SampleSummaries,
  SpendCategoryNode,
  SpendMerchant,
  SpendTransaction,
} from '../types/financial';
import { estimatePaymentSplit } from '../utils/loanAmortization';
import { longTermReturnPct } from '../utils/householdFromDraft';
import { getCategoryColor } from './categoryColors';

const CATEGORY_LABELS: Record<string, string> = {
  household: 'Household',
  groceries: 'Groceries',
  dining: 'Dining',
  transport: 'Transport',
  subscriptions: 'Subscriptions',
  entertainment: 'Entertainment',
  health: 'Health',
  shopping: 'Shopping',
  utilities: 'Utilities',
  other: 'Other',
};

const ALLOCATION_COLORS = {
  day_to_day: '#6366F1',
  debt_payments: '#F59E0B',
  active_investing: '#3DD6A4',
  savings_cash: '#38BDF8',
} as const;

const HOME_APPRECIATION_PCT = 3;
const CAR_DEPRECIATION_PCT = 15;
const CASH_YIELD_PCT = 2;
const PROJECTION_YEARS = 20;
const SERIES_STEP_YEARS = 2;

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function categoryLabel(categoryId: string): string {
  return CATEGORY_LABELS[categoryId] ?? categoryId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseYearMonth(asOfMonth: string): { year: number; month: number } {
  const [y, m] = asOfMonth.split('-').map((n) => Number(n));
  return {
    year: Number.isFinite(y) ? y : new Date().getFullYear(),
    month: Number.isFinite(m) && m >= 1 && m <= 12 ? m : 1,
  };
}

function resolvePeriodKey(
  profile: CoreHousehold['profile'],
  options?: DeriveHouseholdOptions,
): { period: HouseholdPeriodKind; periodKey: string } {
  const period = options?.period ?? 'month';
  const asOf = profile.asOfMonth;
  if (options?.periodKey) {
    return { period, periodKey: options.periodKey };
  }
  if (period === 'year' || period === 'ytd') {
    return { period, periodKey: asOf.slice(0, 4) };
  }
  return { period, periodKey: asOf };
}

/** Inclusive month count covered by the selected period. */
export function periodMonthCount(
  period: HouseholdPeriodKind,
  periodKey: string,
  asOfMonth: string,
): number {
  const asOf = parseYearMonth(asOfMonth);
  if (period === 'month') return 1;
  if (period === 'year') return 12;
  // ytd: Jan through asOf month within periodKey year
  const year = Number(periodKey) || asOf.year;
  if (year < asOf.year) return 12;
  if (year > asOf.year) return 0;
  return asOf.month;
}

function transactionInPeriod(
  tx: CoreTransaction,
  period: HouseholdPeriodKind,
  periodKey: string,
  asOfMonth: string,
): boolean {
  const date = tx.date;
  if (period === 'month') {
    return date.startsWith(periodKey);
  }
  if (period === 'year') {
    return date.startsWith(periodKey);
  }
  // ytd: from Jan 1 of periodKey year through end of asOfMonth (if same year)
  const year = periodKey.slice(0, 4);
  if (!date.startsWith(year)) return false;
  const asOf = parseYearMonth(asOfMonth);
  if (Number(year) < asOf.year) return true;
  if (Number(year) > asOf.year) return false;
  const end = `${asOfMonth}-31`;
  return date <= end;
}

function isExpense(tx: CoreTransaction): boolean {
  return (tx.kind ?? 'expense') === 'expense';
}

function buildSpendTree(transactions: CoreTransaction[]): SpendCategoryNode[] {
  type MerchantAcc = {
    name: string;
    amount: number;
    transactions: SpendTransaction[];
  };
  type CatAcc = {
    id: string;
    name: string;
    amount: number;
    merchants: Map<string, MerchantAcc>;
  };

  const categories = new Map<string, CatAcc>();

  for (const tx of transactions) {
    if (!isExpense(tx)) continue;
    let cat = categories.get(tx.categoryId);
    if (!cat) {
      cat = {
        id: tx.categoryId,
        name: categoryLabel(tx.categoryId),
        amount: 0,
        merchants: new Map(),
      };
      categories.set(tx.categoryId, cat);
    }
    cat.amount = roundMoney(cat.amount + tx.amount);

    const merchantKey = slugify(tx.merchant) || 'unknown';
    let merchant = cat.merchants.get(merchantKey);
    if (!merchant) {
      merchant = { name: tx.merchant, amount: 0, transactions: [] };
      cat.merchants.set(merchantKey, merchant);
    }
    merchant.amount = roundMoney(merchant.amount + tx.amount);
    merchant.transactions.push({
      id: tx.id,
      date: tx.date,
      merchant: tx.merchant,
      amount: tx.amount,
      note: tx.note,
    });
  }

  const nodes: SpendCategoryNode[] = [];
  let colorIndex = 0;
  for (const cat of categories.values()) {
    const merchants: SpendMerchant[] = [...cat.merchants.entries()]
      .map(([key, m]) => ({
        id: `m_${key}`,
        name: m.name,
        amount: m.amount,
        transactions: [...m.transactions].sort((a, b) => b.date.localeCompare(a.date)),
      }))
      .sort((a, b) => b.amount - a.amount);

    nodes.push({
      id: cat.id,
      name: cat.name,
      amount: cat.amount,
      color: getCategoryColor(cat.id, colorIndex),
      merchants,
    });
    colorIndex += 1;
  }

  return nodes.sort((a, b) => b.amount - a.amount);
}

function deriveNetWorth(accounts: CoreAccount[]): SampleNetWorth {
  const assets: SampleAsset[] = accounts
    .filter((a) => a.kind === 'asset')
    .map((a) => ({
      id: a.id,
      type: a.type,
      label: a.label,
      value: a.balance,
      isInvestment: Boolean(a.isInvestment),
      expectedReturnPct: a.expectedReturnPct,
      monthlyContribution: a.monthlyContribution,
    }));

  const liabilities: SampleLiability[] = accounts
    .filter((a) => a.kind === 'liability')
    .map((a) => {
      const split =
        a.balance > 0 && a.interestRatePct != null && a.monthlyPayment != null
          ? estimatePaymentSplit(a.balance, a.interestRatePct, a.monthlyPayment)
          : undefined;
      return {
        id: a.id,
        type: a.type,
        label: a.label,
        balance: a.balance,
        interestRatePct: a.interestRatePct,
        monthlyPayment: a.monthlyPayment,
        monthlyInterest: split?.monthlyInterest,
        monthlyPrincipal: split?.monthlyPrincipal,
      };
    });

  const activeInvestments = assets
    .filter((a) => a.isInvestment)
    .reduce((sum, a) => sum + a.value, 0);
  const passiveInvestments = assets
    .filter((a) => !a.isInvestment)
    .reduce((sum, a) => sum + a.value, 0);
  const totalLiabilities = liabilities.reduce((sum, l) => sum + l.balance, 0);

  return {
    total: roundMoney(activeInvestments + passiveInvestments - totalLiabilities),
    activeInvestments: roundMoney(activeInvestments),
    passiveInvestments: roundMoney(passiveInvestments),
    totalLiabilities: roundMoney(totalLiabilities),
    assets,
    liabilities,
  };
}

function sumLiabilityPayments(accounts: CoreAccount[]): number {
  return accounts
    .filter((a) => a.kind === 'liability')
    .reduce((sum, a) => sum + (a.monthlyPayment ?? 0), 0);
}

function sumInvestmentContributions(accounts: CoreAccount[]): number {
  return accounts
    .filter((a) => a.kind === 'asset' && a.isInvestment)
    .reduce((sum, a) => sum + (a.monthlyContribution ?? 0), 0);
}

function sumCashContributions(accounts: CoreAccount[]): number {
  return accounts
    .filter((a) => a.kind === 'asset' && !a.isInvestment && a.type === 'cash')
    .reduce((sum, a) => sum + (a.monthlyContribution ?? 0), 0);
}

/** Build Home “Where every dollar goes” buckets from monthly cash-flow parts. */
export function buildDollarAllocation(params: {
  monthlyIncome: number;
  dayToDaySpend: number;
  debtPayment: number;
  investing: number;
  cashSavings?: number;
}): DollarAllocationBucket[] {
  const income = Math.max(0, params.monthlyIncome);
  if (income <= 0) {
    return [];
  }

  const dayToDay = Math.max(0, params.dayToDaySpend);
  const debt = Math.max(0, params.debtPayment);
  const investing = Math.max(0, params.investing);
  // Residual to cash so buckets cover take-home (may absorb rounding / unallocated).
  const allocated = dayToDay + debt + investing;
  const cash = Math.max(0, income - allocated);

  const raw = [
    { id: 'day_to_day', label: 'Day-to-day spend', amount: dayToDay, color: ALLOCATION_COLORS.day_to_day },
    {
      id: 'debt_payments',
      label: 'Housing / mortgage',
      amount: debt,
      color: ALLOCATION_COLORS.debt_payments,
    },
    {
      id: 'active_investing',
      label: 'Active investing',
      amount: investing,
      color: ALLOCATION_COLORS.active_investing,
    },
    {
      id: 'savings_cash',
      label: 'Savings / cash',
      amount: cash > 0 ? cash : Math.max(0, params.cashSavings ?? 0),
      color: ALLOCATION_COLORS.savings_cash,
    },
  ];

  return allocatePercents(raw, income);
}

/** Largest-remainder so percent shares always sum to 100. */
function allocatePercents(
  buckets: Omit<DollarAllocationBucket, 'percent'>[],
  income: number,
): DollarAllocationBucket[] {
  const amountSum = buckets.reduce((sum, b) => sum + Math.max(0, b.amount), 0) || 1;
  const shares = buckets.map((bucket) => {
    const exact = (Math.max(0, bucket.amount) / amountSum) * 100;
    const floor = Math.floor(exact);
    return { bucket, floor, frac: exact - floor };
  });

  let remaining = 100 - shares.reduce((sum, s) => sum + s.floor, 0);
  const ranked = [...shares].sort((a, b) => b.frac - a.frac);
  const bonus = new Map<string, number>();
  for (const row of ranked) {
    if (remaining <= 0) break;
    bonus.set(row.bucket.id, 1);
    remaining -= 1;
  }

  return shares.map(({ bucket, floor }) => {
    const percent = floor + (bonus.get(bucket.id) ?? 0);
    // Prefer residual-based amounts when they already cover income; otherwise scale.
    const amount =
      amountSum <= income || income <= 0
        ? roundMoney(bucket.amount)
        : roundMoney((percent / 100) * income);
    return { ...bucket, amount, percent };
  });
}

function buildSummaries(params: {
  period: HouseholdPeriodKind;
  spendTree: SpendCategoryNode[];
  dayToDaySpend: number;
  debtPayment: number;
  monthlyIncome: number;
  months: number;
}): SampleSummaries {
  const income = params.monthlyIncome * params.months;
  const totalSpend = roundMoney(params.dayToDaySpend + params.debtPayment);
  const totalSaved = roundMoney(Math.max(0, income - totalSpend));

  return {
    period: params.period,
    totalSpend,
    totalSaved,
    categories: params.spendTree.map((c) => ({
      id: c.id,
      name: c.name,
      amount: c.amount,
      color: c.color,
    })),
    debtVsSavings: {
      debtPayment: roundMoney(params.debtPayment),
      savingsContribution: totalSaved,
    },
  };
}

function stepLiabilityBalance(
  balance: number,
  interestRatePct: number | undefined,
  monthlyPayment: number | undefined,
  months: number,
): number {
  let b = Math.max(0, balance);
  const payment = monthlyPayment ?? 0;
  const rate = (interestRatePct ?? 0) / 100 / 12;
  for (let i = 0; i < months && b > 0.01; i += 1) {
    const interest = b * rate;
    const principal = Math.min(b, Math.max(0, payment - interest));
    b = Math.max(0, b - principal);
  }
  return roundMoney(b);
}

function stepInvestmentBalance(
  balance: number,
  expectedReturnPct: number | undefined,
  monthlyContribution: number | undefined,
  years: number,
): number {
  const annualRate = (expectedReturnPct ?? 0) / 100;
  const contrib = monthlyContribution ?? 0;
  let b = balance;
  for (let y = 0; y < years; y += 1) {
    b = b * (1 + annualRate) + contrib * 12;
  }
  return roundMoney(b);
}

/** Do not project sample / import contribution plans the user never set. */
function projectionMonthlyContribution(account: CoreAccount): number {
  if (
    account.id.startsWith('synced_') ||
    account.id.startsWith('import_') ||
    account.id.startsWith('asset_')
  ) {
    return 0;
  }
  return Math.max(0, account.monthlyContribution ?? 0);
}

function classifyAssetBucket(
  type: string,
): keyof NetWorthYearRow['assets'] {
  if (type === 'home') return 'home';
  if (type === 'car') return 'car';
  if (type === '401k' || type === 'roth_ira' || type === 'ira' || type === 'retirement') {
    return 'retirement';
  }
  if (type === 'brokerage' || type === 'investment') return 'brokerage';
  if (type === 'cash' || type === 'checking' || type === 'savings') return 'cash';
  return 'other';
}

function classifyLiabilityBucket(
  type: string,
): keyof NetWorthYearRow['liabilities'] {
  if (type === 'mortgage') return 'mortgage';
  if (type === 'auto') return 'auto';
  if (type === 'credit' || type === 'credit_card') return 'creditCards';
  return 'other';
}

function isMortgageLiability(account: CoreAccount): boolean {
  return account.kind === 'liability' && account.type === 'mortgage';
}

function deriveProjections(
  accounts: CoreAccount[],
  startYear: number,
): SampleProjections {
  const mortgage = accounts.find(isMortgageLiability);
  const consumerDebts = accounts.filter(
    (a) => a.kind === 'liability' && !isMortgageLiability(a),
  );

  const mortgageBalanceSeries: { year: number; balance: number }[] = [];
  const debtBalanceSeries: { year: number; balance: number }[] = [];
  const investmentBalanceSeries: { year: number; balance: number }[] = [];
  const netWorthByYear: NetWorthYearRow[] = [];

  let prevNetWorth: number | null = null;
  let crossoverYear = 0;

  for (let offset = 0; offset <= PROJECTION_YEARS; offset += 1) {
    const year = startYear + offset;
    const monthsElapsed = offset * 12;

    const assets: NetWorthYearRow['assets'] = {
      home: 0,
      car: 0,
      retirement: 0,
      brokerage: 0,
      cash: 0,
      other: 0,
    };
    const liabilities: NetWorthYearRow['liabilities'] = {
      mortgage: 0,
      auto: 0,
      creditCards: 0,
      other: 0,
    };

    let investmentTotal = 0;

    for (const account of accounts) {
      if (account.kind === 'asset') {
        let value = account.balance;
        if (account.type === 'home') {
          value = roundMoney(account.balance * Math.pow(1 + HOME_APPRECIATION_PCT / 100, offset));
        } else if (account.type === 'car') {
          value = roundMoney(account.balance * Math.pow(1 - CAR_DEPRECIATION_PCT / 100, offset));
        } else if (account.isInvestment) {
          // Cap YTD/screenshot returns; skip contrib on synced/import rows.
          value = stepInvestmentBalance(
            account.balance,
            longTermReturnPct(account.expectedReturnPct),
            projectionMonthlyContribution(account),
            offset,
          );
          investmentTotal += value;
        } else if (account.type === 'cash' || account.type === 'checking' || account.type === 'savings') {
          value = stepInvestmentBalance(
            account.balance,
            account.expectedReturnPct ?? CASH_YIELD_PCT,
            projectionMonthlyContribution(account),
            offset,
          );
        } else {
          value = stepInvestmentBalance(
            account.balance,
            account.expectedReturnPct,
            projectionMonthlyContribution(account),
            offset,
          );
        }
        assets[classifyAssetBucket(account.type)] += value;
      } else {
        const bal = stepLiabilityBalance(
          account.balance,
          account.interestRatePct,
          account.monthlyPayment,
          monthsElapsed,
        );
        liabilities[classifyLiabilityBucket(account.type)] += bal;
      }
    }

    const assetSum =
      assets.home + assets.car + assets.retirement + assets.brokerage + assets.cash + assets.other;
    const liabSum =
      liabilities.mortgage + liabilities.auto + liabilities.creditCards + liabilities.other;
    const netWorth = roundMoney(assetSum - liabSum);
    const netWorthDelta = prevNetWorth == null ? 0 : roundMoney(netWorth - prevNetWorth);
    prevNetWorth = netWorth;

    netWorthByYear.push({
      year,
      assets: {
        home: roundMoney(assets.home),
        car: roundMoney(assets.car),
        retirement: roundMoney(assets.retirement),
        brokerage: roundMoney(assets.brokerage),
        cash: roundMoney(assets.cash),
        other: roundMoney(assets.other),
      },
      liabilities: {
        mortgage: roundMoney(liabilities.mortgage),
        auto: roundMoney(liabilities.auto),
        creditCards: roundMoney(liabilities.creditCards),
        other: roundMoney(liabilities.other),
      },
      netWorth,
      netWorthDelta,
    });

    if (offset % SERIES_STEP_YEARS === 0 || offset === PROJECTION_YEARS) {
      const mortBal = mortgage
        ? stepLiabilityBalance(
            mortgage.balance,
            mortgage.interestRatePct,
            mortgage.monthlyPayment,
            monthsElapsed,
          )
        : 0;
      const consumerDebtBal = roundMoney(
        consumerDebts.reduce(
          (sum, debt) =>
            sum +
            stepLiabilityBalance(
              debt.balance,
              debt.interestRatePct,
              debt.monthlyPayment,
              monthsElapsed,
            ),
          0,
        ),
      );
      // debtBalanceSeries = all liabilities (home loan + consumer) for NW-aligned charts
      const totalDebtBal = roundMoney(mortBal + consumerDebtBal);
      mortgageBalanceSeries.push({ year, balance: mortBal });
      debtBalanceSeries.push({ year, balance: totalDebtBal });
      investmentBalanceSeries.push({ year, balance: roundMoney(investmentTotal) });

      // Crossover = investments overtake the home loan (primary liability).
      if (
        crossoverYear === 0 &&
        offset > 0 &&
        investmentTotal >= mortBal &&
        mortBal >= 0
      ) {
        crossoverYear = year;
      }
    }
  }

  if (crossoverYear === 0 && mortgage) {
    for (const row of netWorthByYear) {
      const inv = row.assets.retirement + row.assets.brokerage;
      if (row.year > startYear && inv >= row.liabilities.mortgage) {
        crossoverYear = row.year;
        break;
      }
    }
  }

  const tenYear = netWorthByYear.find((r) => r.year === startYear + 10);
  const netWorthIn10Years = tenYear?.netWorth ?? netWorthByYear[netWorthByYear.length - 1]?.netWorth ?? 0;

  // Drop year-0-only trailing empty mortgage series points after payoff for chart clarity
  const lastMortWithBalance = [...mortgageBalanceSeries]
    .reverse()
    .find((p) => p.balance > 0);
  const trimmedMortgage =
    lastMortWithBalance == null
      ? mortgageBalanceSeries.slice(0, 1)
      : mortgageBalanceSeries.filter(
          (p) => p.year <= lastMortWithBalance.year + SERIES_STEP_YEARS,
        );

  const lastDebtWithBalance = [...debtBalanceSeries].reverse().find((p) => p.balance > 0);
  const trimmedDebt =
    lastDebtWithBalance == null
      ? debtBalanceSeries.slice(0, 1)
      : debtBalanceSeries.filter(
          (p) => p.year <= lastDebtWithBalance.year + SERIES_STEP_YEARS,
        );

  return {
    crossoverYear,
    netWorthIn10Years,
    mortgageBalanceSeries: trimmedMortgage.length > 0 ? trimmedMortgage : mortgageBalanceSeries,
    debtBalanceSeries: trimmedDebt.length > 0 ? trimmedDebt : debtBalanceSeries,
    investmentBalanceSeries,
    netWorthByYear,
  };
}

function buildProfile(profile: CoreHousehold['profile']): SampleProfile {
  return {
    countryCode: profile.countryCode,
    currency: profile.currency,
    annualSalary: roundMoney(profile.monthlyIncome * 12),
    monthlyIncome: profile.monthlyIncome,
    savingsRateTarget: profile.savingsRateTarget,
    householdName: profile.householdName,
  };
}

/**
 * Derive UI view models from lean core household entities.
 */
export function deriveHousehold(
  core: CoreHousehold,
  options?: DeriveHouseholdOptions,
): DerivedHouseholdView {
  const { period, periodKey } = resolvePeriodKey(core.profile, options);
  const months = Math.max(1, periodMonthCount(period, periodKey, core.profile.asOfMonth));

  const periodTxs = core.transactions.filter((tx) =>
    transactionInPeriod(tx, period, periodKey, core.profile.asOfMonth),
  );
  const spendTree = buildSpendTree(periodTxs);
  const dayToDaySpend = roundMoney(spendTree.reduce((sum, c) => sum + c.amount, 0));

  // Scale fixed monthly debt to months that actually have expense data when the
  // fixture (or live ledger) only partially covers the calendar period.
  const expenseMonths = new Set(
    periodTxs.filter(isExpense).map((tx) => tx.date.slice(0, 7)),
  );
  const summaryMonths = Math.max(
    1,
    expenseMonths.size > 0 ? expenseMonths.size : months,
  );

  const monthlyDebt = sumLiabilityPayments(core.accounts);
  const monthlyInvesting = sumInvestmentContributions(core.accounts);
  const monthlyCash = sumCashContributions(core.accounts);
  const debtPayment = roundMoney(monthlyDebt * summaryMonths);

  // Allocation is always framed as a single take-home month (UI buckets).
  const dollarAllocation = buildDollarAllocation({
    monthlyIncome: core.profile.monthlyIncome,
    dayToDaySpend:
      period === 'month'
        ? dayToDaySpend
        : roundMoney(dayToDaySpend / summaryMonths),
    debtPayment: monthlyDebt,
    investing: monthlyInvesting,
    cashSavings: monthlyCash,
  });

  const summaries = buildSummaries({
    period,
    spendTree,
    dayToDaySpend,
    debtPayment,
    monthlyIncome: core.profile.monthlyIncome,
    months: summaryMonths,
  });

  const netWorth = deriveNetWorth(core.accounts);
  const startYear = parseYearMonth(core.profile.asOfMonth).year;
  const projections = deriveProjections(core.accounts, startYear);

  return {
    profile: buildProfile(core.profile),
    summaries,
    dollarAllocation,
    netWorth,
    spendTree,
    projections,
  };
}

export default deriveHousehold;
